import { Container, Grid, Paper, Theme, Typography, Card, CardContent, CardHeader, Avatar, Link } from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import clsx from 'clsx';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { IEvent } from '../../models/IEvent';
import UserAdminServiceInstance from '../../services/UserAdminService';
import EventList from '../events/EventList';
import { deepPurple } from '@mui/material/colors';
import { IOrganizer } from '../../models/IOrganizer';
import SeasonSelectorControl from '../common/SeasonSelectorControl';
import OntimeBreadcrumb from '../common/OntimeBreadcrumb';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            backgroundColor: theme.palette.background.paper,
        },
        inline: {
            display: 'inline',
        },
        content: {
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
        },
        container: {
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
        },
        paper: {
            padding: theme.spacing(2),
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column'
        },
        appBarSpacer: theme.mixins.toolbar,
        fixedHeight: {
            height: 240,
        },
    }));

function OrganizerDetail() {
    const { organizerId } = useParams();
    const [organizer, setOrganizer] = useState<IOrganizer>();
    const [events, setEvents] = useState<IEvent[]>([]);
    const [selectedSeason, setSelectedSeason] = useState<number>(new Date().getFullYear());
    const classes = useStyles();
    const { t } = useTranslation("common");

    useEffect(() => {
        if (!organizerId) return;
        const source = axios.CancelToken.source();
        const fetchData = async () => {
            const organizer = await UserAdminServiceInstance.getOrganizer(organizerId, source.token);
            setOrganizer(organizer);
        };
        fetchData().catch(reason => {
            if (!axios.isCancel(reason)) {
                console.error(reason);
            }
        });
        return () => { source.cancel(); };
    }, [organizerId]);

    useEffect(() => {
        if (!organizerId) return;
        const source = axios.CancelToken.source();
        const fetchEvents = async () => {
            const from = new Date(selectedSeason, 0, 1);
            const to = new Date(selectedSeason, 11, 31, 23, 59, 59, 999);
            const evts = await UserAdminServiceInstance.getEvents(organizerId, from, to, source.token);
            setEvents(evts);
        };
        fetchEvents().catch(reason => {
            if (!axios.isCancel(reason)) {
                console.error(reason);
            }
        });
        return () => { source.cancel(); };
    }, [organizerId, selectedSeason]);

    const fixedHeightPaper = clsx(classes.fixedHeight);
    const liveAndPastEvents = events?.filter(e => e.status === 1 || e.status === 2) ?? [];
    const upcomingEvents = events?.filter(e => e.status === 0) ?? [];
    const organizersNameMap: Record<string, string> = { [t('mainList.organizers')]: '/organizers' };
    const isCurrentSeason = selectedSeason === new Date().getFullYear();

    return (
        <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            <Container maxWidth="lg" className={classes.container}>
                <Grid container spacing={3} direction="column">
                    {/* Organizer Details */}
                    <Grid item xs={12}>
                        <OntimeBreadcrumb nameMap={organizersNameMap} current={organizer?.name ?? ''} />
                    </Grid>
                    <Grid item xs={12}>
                        <Card>
                            <CardHeader
                                avatar={
                                    <Avatar
                                        sx={{ bgcolor: deepPurple[500] }}
                                        src={organizer?.pictureUrl || undefined} // Use PictureUrl if available
                                    >
                                        {!organizer?.pictureUrl && organizer?.name?.charAt(0).toUpperCase()} {/* Fallback to first letter */}
                                    </Avatar>
                                }
                                title={organizer?.name}
                                subheader={
                                    organizer?.webPage ? (
                                        <Link
                                            href={organizer.webPage}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                            color="inherit"
                                        >
                                            {organizer.webPage}
                                        </Link>
                                    ) : (
                                        t("organizer.noWebPage")
                                    )
                                }
                            />
                            <CardContent>
                                <Typography variant="body2" color="textSecondary">
                                    {t("organizer.lastSeasonEvents")}: {organizer?.eventsLastSeason}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {t("organizer.allEvents")}: {organizer?.eventsInTotal}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Events Section */}
                    <Grid container item spacing={3}>
                        {/* Season selector */}
                        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <SeasonSelectorControl value={selectedSeason} onChange={setSelectedSeason} />
                        </Grid>
                        {/* Live & past events */}
                        <Grid item xs={12} md={isCurrentSeason ? 6 : 12}>
                            <Typography>{t("events.pastEvents")}</Typography>
                            <Paper className={fixedHeightPaper}>
                                <EventList events={liveAndPastEvents} />
                            </Paper>
                        </Grid>

                        {/* Upcoming events - only shown for current season */}
                        {isCurrentSeason && (
                            <Grid item xs={12} md={6}>
                                <Typography>{t("events.upcomingEvents")}</Typography>
                                <Paper className={fixedHeightPaper}>
                                    <EventList events={upcomingEvents} />
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Container>
        </main>
    );
}

export default OrganizerDetail;
